// 32-entrance (pada) Vastu guidance.
// Each of the 32 boundary padas (N1–N8, E1–E8, S1–S8, W1–W8) is ruled by a
// specific deity/energy that shapes the effect of a main entrance placed there.
// Orientation matches lib/sectors.js: N1 sits near NW → N8 near NE, E1 near NE
// → E8 near SE, S1 near SE → S8 near SW, W1 near SW → W8 near NW.

// grade: "ideal" (auspicious) | "neutral" (mixed, needs care) | "avoid" (inauspicious)
export const GRADE_META = {
  ideal: { label: "Auspicious", short: "Ideal", color: "#10B981" },
  neutral: { label: "Mixed", short: "Neutral", color: "#F59E0B" },
  avoid: { label: "Inauspicious", short: "Avoid", color: "#EF4444" },
};

// code → { deity, grade, effect }
export const PADAS = {
  // North wall (NW → NE)
  N1: { deity: "Roga", grade: "avoid", effect: "Entrance of disease. Invites chronic ill-health and drains the family's vitality." },
  N2: { deity: "Naga (Ahi)", grade: "avoid", effect: "Brings cruelty, mental stress and hidden enemies; disturbs peace of mind." },
  N3: { deity: "Mukhya", grade: "ideal", effect: "All-round growth. Opens new opportunities, skills and steady progress." },
  N4: { deity: "Bhallata", grade: "ideal", effect: "Abundance of wealth and material prosperity; excellent for business gains." },
  N5: { deity: "Soma (Kubera)", grade: "ideal", effect: "The treasury gate. Wealth, health and favours from authority and government." },
  N6: { deity: "Bhujaga", grade: "avoid", effect: "Fear of losing money and hidden financial troubles; secrets and worry." },
  N7: { deity: "Aditi", grade: "avoid", effect: "Dejection and depression; lack of support and low self-worth." },
  N8: { deity: "Diti", grade: "avoid", effect: "Clouded vision and poor decisions; plans lack clarity and direction." },

  // East wall (NE → SE)
  E1: { deity: "Shikhi", grade: "avoid", effect: "Fire hazards, accidents and anxiety; risk of sudden mishaps." },
  E2: { deity: "Parjanya", grade: "ideal", effect: "Fresh ideas, progeny and new beginnings; attracts opportunities." },
  E3: { deity: "Jayanta", grade: "ideal", effect: "Victory and success; steady gains and recognition for effort." },
  E4: { deity: "Indra (Mahendra)", grade: "ideal", effect: "Leadership and fame; strong connection with authority and prosperity." },
  E5: { deity: "Surya (Aditya)", grade: "ideal", effect: "Power, willpower and recognition; commanding presence and drive." },
  E6: { deity: "Satya", grade: "neutral", effect: "Demands honesty; misuse leads to loss of credibility and false commitments." },
  E7: { deity: "Bhrisha", grade: "avoid", effect: "Anxiety, indecision and instability; scattered focus and unrest." },
  E8: { deity: "Akasha (Antariksha)", grade: "avoid", effect: "A sense of emptiness and void; losses, sorrow and lack of grounding." },

  // South wall (SE → SW)
  S1: { deity: "Anila", grade: "avoid", effect: "Instability and restlessness; adverse effects on male children." },
  S2: { deity: "Pusha", grade: "neutral", effect: "Broadly neutral; can steady a settled home but weak for new ventures." },
  S3: { deity: "Vitatha", grade: "neutral", effect: "Brings gains but tempts unfair means and unreliability; use with care." },
  S4: { deity: "Grihakshata", grade: "ideal", effect: "Fame, wealth and stability for a disciplined household; a strong south gate." },
  S5: { deity: "Yama", grade: "avoid", effect: "The most feared south pada. Debt, chronic illness and heavy losses." },
  S6: { deity: "Gandharva", grade: "neutral", effect: "Favours artists and performers, yet can invite humiliation or scarcity." },
  S7: { deity: "Bhringaraja", grade: "avoid", effect: "Wasted effort, chronic fatigue and gradual decline of health." },
  S8: { deity: "Mriga", grade: "avoid", effect: "Loneliness, loss of wealth and sorrow to children; highly inauspicious." },

  // West wall (SW → NW)
  W1: { deity: "Pitra", grade: "avoid", effect: "Ancestral karmic burden; poverty and a shortened, struggling life path." },
  W2: { deity: "Dwarpal (Dauwarik)", grade: "neutral", effect: "Largely neutral; may cause job instability or relationship friction." },
  W3: { deity: "Sugriva", grade: "ideal", effect: "Highly auspicious. Brings remarkable wealth, growth and prosperity." },
  W4: { deity: "Pushpadanta", grade: "ideal", effect: "Steady wealth and success; especially favourable for male children." },
  W5: { deity: "Varuna", grade: "neutral", effect: "Mixed results; perfectionism and global trade, but risk of over-ambition." },
  W6: { deity: "Asura", grade: "avoid", effect: "Conflict, aggression and addictions; disturbs harmony and judgement." },
  W7: { deity: "Shosha", grade: "avoid", effect: "Depletion of resources, financial stress and lingering health issues." },
  W8: { deity: "Papa (Papyakshma)", grade: "avoid", effect: "Highly inauspicious; illness, moral decline and unlawful tendencies." },
};

const PADA_RE = /^([NESW])([1-8])$/;

// Return pada guidance for a 32-division code, else null.
export function getPadaInfo(code) {
  const m = PADA_RE.exec(code || "");
  if (!m) return null;
  const data = PADAS[code];
  if (!data) return null;
  return { code, side: m[1], index: Number(m[2]), ...data, grade: data.grade, gradeMeta: GRADE_META[data.grade] };
}
